import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import { SnapshotService } from '../Services/snapshot.service';
import { CustomsnapshotService } from '../Services/customsnapshot.service';
import { SnapshotSharedVariablesService } from '../Services/snapshot-shared-variables.service';
import { Observable, Subject, map, shareReplay, takeUntil } from 'rxjs';
import { AlertMessageService } from 'src/app/common/services/alert-message.service';
import { CONFIG_TYPE, TopicsMode } from './topics-configuration.model';
import { TopicsGridComponent } from './topics-grid/topics-grid.component';

@Component({
  selector: 'app-topics-configuration',
  templateUrl: './topics-configuration.component.html',
  styleUrls: ['./topics-configuration.component.css']
})

export class TopicsConfigurationComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild(TopicsGridComponent) grid?: TopicsGridComponent;
  @Input() snapshot_details: any;
  isLoading = false;
  mandatoryVariableMapping: any = [];
  private destroy$ = new Subject<void>();
  dimensionList: any;
  hasSnapshotConfiguration: boolean = false;
  topicsLoaded = false;

  // ES partial update replaces the whole rows array, so rows of the other config type must be resent on save
  private savedTopicsRows: any[] = [];
  defaultSavedRows: any[] = [];
  bipsSavedRows: any[] = [];

  // Loader is driven by a pending-request counter so overlapping calls never leave it stuck.
  private pendingRequests = 0;
  // Row-independent responses are fetched once and shared across all rows to avoid flooding the API.
  private widgetLibrary$?: Observable<any>;
  private entryPointData$?: Observable<any[]>;
  private leftPanel$ = new Map<string, Observable<any[]>>();

  constructor(
    private _configService: SnapshotService,
    private _customConfigService: CustomsnapshotService,
    private clientDetails: SnapshotSharedVariablesService,
    private _alertService: AlertMessageService
  ) { }

  ngOnInit(): void {
    const client = this.clientDetails.clientDetails.client_name;
    const study = this.clientDetails.clientDetails.selectedStudy;
    if (!client || !study) return;
    this.initialLoad();
    this.loadCustomSnapshotConfiguration();
  }

  private startLoading(): void {
    this.pendingRequests++;
    this.isLoading = true;
  }

  private stopLoading(): void {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    this.isLoading = this.pendingRequests > 0;
  }

  private getWidgetLibrary(): Observable<any> {
    if (!this.widgetLibrary$) {
      this.widgetLibrary$ = this._customConfigService
        .getCustomSnapshotWidgetLibrary({ ClientName: this.clientDetails.clientDetails.client_name, SnapshotName: this.clientDetails.clientDetails.snapshotselected })
        .pipe(map((c: any) => (c?.Data ? JSON.parse(c.Data) : null)), shareReplay(1));
    }
    return this.widgetLibrary$;
  }

  private getEntryPointData(): Observable<any[]> {
    if (!this.entryPointData$) {
      const studyName = this.clientDetails.clientDetails.selectedStudy;
      this.entryPointData$ = this._configService
        .getSnapshotEntryPointData({ snapshot_name: this.clientDetails.clientDetails.snapshotselected, client_name: this.clientDetails.clientDetails.client_name, client_code: this.clientDetails.clientDetails.client_name })
        .pipe(
          map((c: any) => (c?.Data ? (JSON.parse(c.Data).EntryPoints || []).filter((item: any) => item.study === studyName) : [])),
          shareReplay(1)
        );
    }
    return this.entryPointData$;
  }

  private getLeftPanelData(payload: any[]): Observable<any[]> {
    const key = JSON.stringify(payload);
    let obs = this.leftPanel$.get(key);
    if (!obs) {
      obs = this._configService.getSnapshotLeftPanelData(payload)
        .pipe(map((data: any) => (data?.Data ? JSON.parse(data.Data) : [])), shareReplay(1));
      this.leftPanel$.set(key, obs);
    }
    return obs;
  }

  loadCustomSnapshotConfiguration(): void {
    const clientName = this.clientDetails.clientDetails.client_name;
    const snapshotName = this.clientDetails.clientDetails.snapshotselected;

    if (!clientName || !snapshotName) {
      this.topicsLoaded = true;
      return;
    }

    this._customConfigService.getCustomSnapshotWidgetLibrary({
      ClientName: clientName,
      SnapshotName: snapshotName
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        try {
          const data = typeof response.Data === 'string' ? JSON.parse(response.Data) : response.Data;
          this.hasSnapshotConfiguration = !!data && Object.keys(data).length > 0;

          // Load topics configuration only if snapshot is configured
          if (this.hasSnapshotConfiguration) {
            this.loadTopicsConfiguration();
          } else {
            this.topicsLoaded = true;
          }
        } catch (error) {
          console.error('Error parsing snapshot configuration:', error);
          this.hasSnapshotConfiguration = false;
          this.topicsLoaded = true;
        }
      },
      error: (err: any) => {
        console.error('Load custom snapshot configuration error:', err);
        this.hasSnapshotConfiguration = false;
        this.topicsLoaded = true;
      }
    });
  }

  loadTopicsConfiguration(): void {
    const clientName = this.clientDetails.clientDetails.client_name;
    const studyName = this.clientDetails.clientDetails.selectedStudy;
    const snapshotName = this.clientDetails.clientDetails.snapshotselected;

    this._configService.getTopicsConfiguration(clientName, studyName, snapshotName).subscribe({
      next: (response: any) => {
        const data =
          typeof response.Data === 'string'
            ? JSON.parse(response.Data)
            : response.Data;

        this.setSavedRows(data?.rows || []);
        this.topicsLoaded = true;
      },
      error: (err: any) => {
        console.error('Load topics error:', err);
        this.topicsLoaded = true;
      }
    });
  }

  selectedBrand = 'default';

  brandOptions = [
    { label: 'Default Brands', value: 'default' },
    { label: 'BIPS Calculation', value: 'bipsCalculation' },
  ];

  allSnapshotTypeOptions = [
    { label: 'Multi Brand', value: 'multiBrand' },
    { label: 'Single Brand', value: 'singleBrand' },
    { label: 'Multi Category', value: 'multiCategory' },
    { label: 'Multi Market', value: 'multiMarket' },
    { label: 'Audience Snapshot', value: 'audienceSnapshot' },
  ];

  snapshotTypeOptions = [
    { label: 'Multi Brand', value: 'multiBrand' },
  ];

  get activeMode(): TopicsMode {
    return this.selectedBrand === 'bipsCalculation' ? 'bips' : 'default';
  }

  isSelectedBrandConfigured(): boolean {
    // Right after a mode switch the old grid is still referenced for one change-detection pass
    if (this.grid?.mode === this.activeMode) {
      return this.grid.hasConfiguredSection();
    }
    const saved = this.activeMode === 'bips' ? this.bipsSavedRows : this.defaultSavedRows;
    return saved.some(r => {
      const section = Array.isArray(r.sectionName) ? r.sectionName[0] : r.sectionName;
      return !!section && section.toString().trim() !== '';
    });
  }

  private isOfType(row: any, configType: string): boolean {
    return (row?.configType ?? CONFIG_TYPE.default) === configType;
  }

  private setSavedRows(rows: any[]): void {
    this.savedTopicsRows = rows;
    this.defaultSavedRows = rows.filter(r => this.isOfType(r, CONFIG_TYPE.default) && this.isMultiBrandSnapshotType(r.snapshotType));
    this.bipsSavedRows = rows.filter(r => this.isOfType(r, CONFIG_TYPE.bips));
  }

  private normalizeExcelKey(value: string): string {
    return (value ?? '').toString().replace(/[\s_\-()]/g, '').toLowerCase();
  }

  isMultiBrandSnapshotType(snapshotType: string): boolean {
    const normalized = this.normalizeExcelKey(snapshotType);
    return normalized === 'multibrand'
      || normalized === 'multiplebrand'
      || normalized === 'multiplebrands';
  }

  getSelectedBrandLabel(): string {
    return this.brandOptions.find(o => o.value === this.selectedBrand)?.label ?? this.selectedBrand;
  }

  initialLoad(): void {
    let parameter1 = {
      study: this.clientDetails.clientDetails.selectedStudy,
      client_name: this.clientDetails.clientDetails.client_name,
      client_code: this.clientDetails.clientDetails.client_name,
      isBrandAnalysis: false,
      dataProces: null,
      dataSource: null,
      fileType: null,
      dataFormat: null,
      moduledetails: null
    };
    this.startLoading();
    this._configService
      .getGeneralConfigData(parameter1)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.stopLoading();
          if (data.Data != '') {
            let lsORisData = JSON.parse(data.Data);
            this.mandatoryVariableMapping = lsORisData?.configData?.mandatory_variable_mapping;
            this.dimensionList = lsORisData?.configData?.dimension ?? [];

          }
        },
        error: () => { this.stopLoading(); }
      })
    if (!this.snapshot_details?.snapshotType_details?.length) return;

    const uniqueTypes: string[] = [];
    this.snapshot_details.snapshotType_details.forEach((detail: any) => {
      if (!uniqueTypes.includes(detail.snapshot_type)) {
        uniqueTypes.push(detail.snapshot_type);
      }
    });

    this.allSnapshotTypeOptions = uniqueTypes.map((type: string) => ({
      label: type,
      value: type
        .split(' ')
        .map((word: string, i: number) =>
          i === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join('')
    }));

    this.snapshotTypeOptions = this.allSnapshotTypeOptions.filter(o =>
      this.isMultiBrandSnapshotType(o.value) || this.isMultiBrandSnapshotType(o.label)
    );

    if (!this.snapshotTypeOptions.length) {
      this.snapshotTypeOptions = [{ label: 'Multi Brand', value: 'multiBrand' }];
    }

  }

  addNewRow(): void {
    this.grid?.addNewRow();
  }

  deleteRows(): void {
    const rowIds = this.grid?.getSelectedRowIds() ?? [];
    if (!rowIds.length) {
      this._alertService.showAlert({ header: 'Warning', message: 'Please select at least one row to delete.', show: true });
      return;
    }

    const payload = {
      clientName: this.clientDetails.clientDetails.client_name,
      studyName: this.clientDetails.clientDetails.selectedStudy,
      snapshotName: this.clientDetails.clientDetails.snapshotselected,
      rowIds
    };

    this.startLoading();
    this._configService.deleteTopicsConfiguration(payload).subscribe({
      next: () => {
        this.stopLoading();
        this.grid?.removeRows(rowIds);
        this.setSavedRows(this.savedTopicsRows.filter(r => !rowIds.includes(r.rowId)));
        this._alertService.showAlert({ header: 'Success', message: 'Deleted Successfully', show: true });
      },
      error: () => {
        this.stopLoading();
        this._alertService.showAlert({ header: 'Error', message: 'Error while deleting', show: true });
      }
    });
  }

  download(): void {
    const selectedRowIds = this.grid?.getSelectedRowIds() ?? [];
    const payload = {
      ClientName: this.clientDetails.clientDetails.client_name,
      StudyName: this.clientDetails.clientDetails.selectedStudy,
      SnapshotName: this.clientDetails.clientDetails.snapshotselected,
      ConfigType: CONFIG_TYPE[this.activeMode],
      RowIds: selectedRowIds
    };

    this.startLoading();

    this._configService.topicsExcelExport(payload)
      .subscribe({
        next: (response: Blob) => {
          this.stopLoading();
          const blob = new Blob([response], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });

          const url = window.URL.createObjectURL(blob);

          const a = document.createElement('a');
          a.href = url;
          a.download = this.activeMode === 'bips' ? 'BIPSCalculationTemplate.xlsx' : 'DefaultBrandsTemplate.xlsx';

          document.body.appendChild(a);
          a.click();

          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);

          this._alertService.showAlert({
            header: 'Success',
            message: 'Template downloaded successfully',
            show: true
          });
        },
        error: () => {

        this.stopLoading();

        this._alertService.showAlert({
          header: 'Error',
          message: 'Unable to download template',
          show: true
        });
      }
    });
}

  triggerUpload(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.grid?.importExcel(file);
    }
    input.value = '';
  }

  submit(): void {
    const payloadRows = this.grid?.getPayloadRows();
    if (!payloadRows) return;

    const activeType = CONFIG_TYPE[this.activeMode];
    const rows = [...payloadRows, ...this.savedTopicsRows.filter(r => !this.isOfType(r, activeType))];

    const finalPayload = {
      data: {
        clientName: this.clientDetails.clientDetails.client_name,
        studyName: this.clientDetails.clientDetails.selectedStudy,
        snapshotName: this.clientDetails.clientDetails.snapshotselected,
        rows
      }
    };

    this._configService.addTopicsConfiguration(finalPayload).subscribe({
      next: (response: any) => {
        console.log(response);
        this.setSavedRows(rows);
        this._alertService.showAlert({ header: 'Success', message: 'Saved Successfully', show: true });
      },
      error: (error: any) => {
        console.error(error);
        this._alertService.showAlert({ header: 'Error', message: 'Error while saving', show: true });
      }
    });
  }

  downloadUserGuide(): void {
    // TODO: implement download user guide
  }
}

